import { useState, useMemo, useEffect } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { TaskCard } from "../components/TaskCard";
import { useUserProgress } from "../hooks/useUserProgress";
import { useTasks } from "../hooks/useTasks";
import { createDomainColorMap } from "../utils/formatting/domainColors";
import { Circle, CheckCircle2, AlertCircle, Search, ArrowUpDown, ChevronDown, Filter, BookOpen, MessageCircle, Map } from "lucide-react";
import type { TelegramUser, Task } from "../types";
import { useMobile } from "../hooks/useMobile";
import { SEOHead } from "../components/SEOHead";
import { generateBreadcrumbSchema } from "../utils/seo";
import { AITutorTaskListPanel } from "../components/AITutorTaskListPanel";
import type { AITutorDualMode } from "../components/AITutorDualModePanel";
import { useResize } from "../hooks/useResize";
import {
  AI_TUTOR_ASIDE_WIDTH_KEY,
  AI_TUTOR_MIN_WIDTH,
  AI_TUTOR_MAX_WIDTH,
  AI_TUTOR_DEFAULT_WIDTH,
  AI_TUTOR_DESKTOP_TOP,
} from "../constants/aiTutorLayout";
import { LAYERS } from "../constants/layers";

/** Отступ списка задач от блока фильтров. Увеличьте значение (в rem), чтобы сдвинуть только список задач правее (фильтры не двигаются). По умолчанию 0. */
const TASKLIST_LIST_OFFSET_LEFT_REM = 0;

const StatusFilter = ({
  selectedStatuses,
  toggleStatus,
  statusCounts,
}: {
  selectedStatuses: string[];
  toggleStatus: (status: string) => void;
  statusCounts: Record<string, number>;
}) => {
  const STATUS_CONFIG: Record<
    string,
    {
      label: string;
      tooltipText?: string;
      icon: React.ComponentType<{ className?: string }>;
      color: string;
      iconColor: string;
    }
  > = {
    idle: {
      label: "Новая задача",
      icon: Circle,
      color: "bg-neutral-light",
      iconColor: "text-neutral-text"
    },
    success: {
      label: "Выполнено",
      icon: CheckCircle2,
      color: "bg-primary",
      iconColor: "text-primary"
    },
    error: {
      label: "С ошибкой",
      tooltipText: "Задачи с неудачной попыткой (решение не совпало с эталоном)",
      icon: AlertCircle,
      color: "bg-red-600",
      iconColor: "text-red-600"
    },
  };

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-neutral-text">Статус выполнения</h3>
      <div className="grid grid-cols-3 gap-2">
        {Object.entries(STATUS_CONFIG).map(([status, config]) => {
          const Icon = config.icon;
          const isSelected = selectedStatuses.includes(status);
          return (
            <div key={status} className="relative group">
              <button
                onClick={() => toggleStatus(status)}
                className={`w-full flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-sm border transition-all ${
                  isSelected
                    ? status === "idle"
                      ? "bg-neutral-600 text-white border-neutral-600"
                      : status === "success"
                        ? "bg-emerald-600/90 text-white border-emerald-600"
                        : "bg-rose-600/90 text-white border-rose-600"
                    : "bg-transparent text-neutral-600 border-neutral-200 hover:bg-neutral-50 hover:border-neutral-300"
                }`}
                aria-describedby={`status-tooltip-${status}`}
              >
                <Icon className={`w-4 h-4 ${isSelected ? "text-white" : config.iconColor}`} />
                <span className="text-xs font-medium">{statusCounts[status] || 0}</span>
              </button>
              <div
                id={`status-tooltip-${status}`}
                className={`absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block bg-neutral-text text-white text-xs rounded-lg px-3 py-2 shadow-xl z-50 pointer-events-none ${status === "error" ? "whitespace-normal max-w-[16rem]" : "whitespace-nowrap"}`}
                role="tooltip"
              >
                {(config.tooltipText ?? config.label)} ({statusCounts[status] || 0})
                <div className="absolute left-1/2 -translate-x-1/2 top-full -mt-1 w-2 h-2 bg-neutral-text rotate-45"></div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SORT_LABELS: Record<string, string> = {
  default: "По умолчанию",
  "difficulty-asc": "Сначала лёгкие",
  "difficulty-desc": "Сначала сложные",
};

const SORT_TITLES: Record<string, string> = {
  default: "Вернуть порядок по умолчанию",
  "difficulty-asc": "Сортировка: от лёгких к сложным",
  "difficulty-desc": "Сортировка: от сложных к лёгким",
};

const SortButton = ({
  sortBy,
  onSortChange,
}: {
  sortBy: "default" | "difficulty-asc" | "difficulty-desc";
  onSortChange: (sort: "default" | "difficulty-asc" | "difficulty-desc") => void;
}) => {
  const cycleSort = () => {
    if (sortBy === "default") {
      onSortChange("difficulty-asc");
    } else if (sortBy === "difficulty-asc") {
      onSortChange("difficulty-desc");
    } else {
      onSortChange("default");
    }
  };

  return (
    <button
      type="button"
      onClick={cycleSort}
      title={SORT_TITLES[sortBy]}
      className="flex items-center gap-2 px-4 py-2 border border-neutral-200 rounded-lg bg-white text-neutral-600 hover:bg-neutral-50 hover:border-neutral-300 transition-colors"
    >
      <ArrowUpDown className="w-4 h-4 text-neutral-500" />
      <span className="text-sm font-medium">{SORT_LABELS[sortBy]}</span>
    </button>
  );
};

const DifficultyFilter = ({
  allDifficulties,
  selectedDifficulties,
  toggleDifficulty,
  difficultyCounts,
}: {
  allDifficulties: string[];
  selectedDifficulties: string[];
  toggleDifficulty: (level: string) => void;
  difficultyCounts: Record<string, number>;
}) => {
  const LABELS: Record<string, string> = {
    easy: "Лёгкие",
    medium: "Средние",
    hard: "Сложные",
  };

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-neutral-text">Уровень сложности</h3>
      <div className="flex flex-wrap gap-2">
        {allDifficulties.map(level => {
          const normalized = level.toLowerCase();
          const isSelected = selectedDifficulties.includes(normalized);

          return (
            <button
              key={normalized}
              onClick={() => toggleDifficulty(normalized)}
              className={`px-3 py-1.5 rounded-lg text-sm border transition-all cursor-pointer ${
                isSelected
                  ? "bg-neutral-600 text-white border-neutral-600"
                  : "bg-transparent text-neutral-600 border-neutral-200 hover:bg-neutral-50 hover:border-neutral-300"
              }`}
            >
              {LABELS[normalized]} ({difficultyCounts[normalized] || 0})
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const TaskList = ({ user }: { user: TelegramUser | null }) => {
  const { progress, syncProgress } = useUserProgress(user);
  const isMobile = useMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tasks, loading } = useTasks();
  const [search, setSearch] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedDifficulties, setSelectedDifficulties] = useState<string[]>([]);
  const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<"default" | "difficulty-asc" | "difficulty-desc">("default");
  const [tagsExpanded, setTagsExpanded] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showAITutor, setShowAITutor] = useState(true);
  const [tutorAsideMode, setTutorAsideMode] = useState<AITutorDualMode>("chat");
  const [chatPanelMinWidth, setChatPanelMinWidth] = useState(380);
  const activeFiltersCount =
    selectedDomains.length +
    selectedStatuses.length +
    selectedDifficulties.length +
    selectedTags.length;
  const aiTutorInitialWidth = useState(() => {
    try {
      const w = localStorage.getItem(AI_TUTOR_ASIDE_WIDTH_KEY);
      if (w) {
        const n = parseInt(w, 10);
        if (n >= AI_TUTOR_MIN_WIDTH && n <= AI_TUTOR_MAX_WIDTH) return n;
      }
    } catch {}
    return AI_TUTOR_DEFAULT_WIDTH;
  })[0];
  const aiTutorResize = useResize({
    initialSize: aiTutorInitialWidth,
    minSize: Math.max(AI_TUTOR_MIN_WIDTH, chatPanelMinWidth),
    maxSize: AI_TUTOR_MAX_WIDTH,
    direction: "horizontal",
    invertHorizontal: true,
    onSizeChange: (s) => {
      try {
        localStorage.setItem(AI_TUTOR_ASIDE_WIDTH_KEY, String(s));
      } catch {}
    },
  });

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const stored = localStorage.getItem("ai-tutor-tasklist-open");
    if (stored !== null) {
      setShowAITutor(mq.matches && stored === "true");
    } else {
      setShowAITutor(mq.matches);
    }
    const handleChange = () => {
      if (!mq.matches) setShowAITutor(false);
    };
    mq.addEventListener("change", handleChange);
    return () => mq.removeEventListener("change", handleChange);
  }, []);
  useEffect(() => {
    localStorage.setItem("ai-tutor-tasklist-open", String(showAITutor));
  }, [showAITutor]);

  // Обновляем прогресс при монтировании компонента или при навигации на эту страницу
  useEffect(() => {
    if (user && location.pathname === "/simulator") {
      // Принудительно синхронизируем прогресс при переходе на страницу TaskList
      // Используем force=true, чтобы инвалидировать кеш и получить свежие данные
      syncProgress(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, user]); // Убрали syncProgress из зависимостей, чтобы избежать бесконечного цикла

  // Инициализация фильтров из URL (ссылки из AI Tutor и шаринга)
  useEffect(() => {
    const domainParam = searchParams.get("domain");
    const difficultyParam = searchParams.get("difficulty");
    const tagParam = searchParams.get("tag");
    const tagsParam = searchParams.get("tags");
    if (domainParam) setSelectedDomains([domainParam]);
    if (difficultyParam) {
      const d = difficultyParam.toLowerCase();
      if (["easy", "medium", "hard"].includes(d)) setSelectedDifficulties([d]);
    }
    const tagValues = tagParam ? [tagParam] : tagsParam ? tagsParam.split(",").map((t) => t.trim()).filter(Boolean) : [];
    if (tagValues.length > 0) setSelectedTags(tagValues);
  }, [searchParams]);

  // Приглушённая палитра доменов на странице списка задач (без яркого акцента)
  const domainColorMap = useMemo(() => {
    const domains = tasks
      .map(task => task.domain)
      .filter((domain): domain is string => Boolean(domain));
    return createDomainColorMap(domains, true);
  }, [tasks]);

  // Hover для приглушённых доменов — нейтральные тона
  const getDomainHoverClasses = (_domainColor: string): string =>
    "hover:bg-neutral-200/80 hover:border-neutral-300";


  const allTags = useMemo(() => {
    const tagsSet = new Set<string>();
    tasks.forEach(task => task.tag?.forEach(tag => tagsSet.add(tag)));
    return Array.from(tagsSet);
  }, [tasks]);

  const allDifficulties = useMemo(() => {
    const levels = new Set<string>();
    tasks.forEach(task => {
      if (task.difficulty) {
        levels.add(task.difficulty.toLowerCase());
      }
    });
    return Array.from(levels);
  }, [tasks]);

  const allDomains = useMemo(() => {
    const domains = new Set<string>();
    tasks.forEach(task => {
      if (task.domain) {
        domains.add(task.domain);
      }
    });
    return Array.from(domains);
  }, [tasks]);

  const toggleArrayValue = (arr: string[], value: string) =>
    arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value];

  // Функция для определения статуса задачи
  const getTaskStatus = (taskId: number): "idle" | "success" | "error" => {
    return progress[taskId]?.status ?? "idle";
  };

  // Подсчет задач по статусам
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { idle: 0, success: 0, error: 0 };
    tasks
      .filter(task => {
        const matchesTags = selectedTags.length === 0 || task.tag?.some(tag => selectedTags.includes(tag));
        const matchesDifficulty =
          selectedDifficulties.length === 0 || 
          (task.difficulty && selectedDifficulties.includes(task.difficulty.toLowerCase()));
        const matchesDomain = selectedDomains.length === 0 || (task.domain && selectedDomains.includes(task.domain));
        return matchesTags && matchesDifficulty && matchesDomain;
      })
      .forEach(task => {
        const status = getTaskStatus(task.id);
        counts[status] = (counts[status] || 0) + 1;
      });
    return counts;
  }, [tasks, selectedTags, selectedDifficulties, selectedDomains, progress]);

  const filteredDifficultyCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    tasks
      .filter(task => {
        const matchesTags = selectedTags.length === 0 || task.tag?.some(tag => selectedTags.includes(tag));
        const matchesDomain = selectedDomains.length === 0 || (task.domain && selectedDomains.includes(task.domain));
        const taskStatus = getTaskStatus(task.id);
        const matchesStatus = selectedStatuses.length === 0 || selectedStatuses.includes(taskStatus);
        return matchesTags && matchesDomain && matchesStatus;
      })
      .forEach(task => {
        if (task.difficulty) {
          const level = task.difficulty.toLowerCase();
          counts[level] = (counts[level] || 0) + 1;
        }
      });
    return counts;
  }, [tasks, selectedTags, selectedDomains, selectedStatuses, progress]);

  // Облако тегов с раскрытием
  const TAGS_TO_SHOW = 10;
  const visibleTags = useMemo(() => {
    return tagsExpanded ? allTags : allTags.slice(0, TAGS_TO_SHOW);
  }, [allTags, tagsExpanded]);
  const hasMoreTags = allTags.length > TAGS_TO_SHOW;

  const filteredTasks = useMemo(() => {
    const taskIdsParam = searchParams.get("task_ids");
    const taskIdsSet =
      taskIdsParam ? new Set(taskIdsParam.split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => !Number.isNaN(n) && n > 0)) : null;

    const filtered = tasks.filter(task => {
      if (taskIdsSet && taskIdsSet.size > 0 && !taskIdsSet.has(task.id)) return false;
      const matchesSearch = task.title.toLowerCase().includes(search.toLowerCase());
      const matchesTags = selectedTags.length === 0 || task.tag?.some(tag => selectedTags.includes(tag));
      const matchesDifficulty =
        selectedDifficulties.length === 0 || 
        (task.difficulty && selectedDifficulties.includes(task.difficulty.toLowerCase()));
      const matchesDomain = selectedDomains.length === 0 || (task.domain && selectedDomains.includes(task.domain));
      const taskStatus = getTaskStatus(task.id);
      const matchesStatus = selectedStatuses.length === 0 || selectedStatuses.includes(taskStatus);
      return matchesSearch && matchesTags && matchesDifficulty && matchesDomain && matchesStatus;
    });

    // Сортировка по сложности
    if (sortBy === "default") {
      return filtered;
    }

    const difficultyOrder: Record<string, number> = {
      easy: 1,
      medium: 2,
      hard: 3,
    };

    return [...filtered].sort((a, b) => {
      const aDifficulty = a.difficulty?.toLowerCase() || "";
      const bDifficulty = b.difficulty?.toLowerCase() || "";
      const aOrder = difficultyOrder[aDifficulty] || 0;
      const bOrder = difficultyOrder[bDifficulty] || 0;
      return sortBy === "difficulty-asc" ? aOrder - bOrder : bOrder - aOrder;
    });
  }, [tasks, search, selectedTags, selectedDifficulties, selectedDomains, selectedStatuses, sortBy, progress, searchParams]);

  /** Каталог задач для чата (подборки) — стабильная ссылка, чтобы не пересоздавать проп при каждом рендере */
  const tasksForChat = useMemo(
    () =>
      tasks.map((t) => ({
        id: t.id,
        title: t.title,
        description: t.description,
        domain: t.domain,
        difficulty: t.difficulty,
        tag: t.tag,
      })),
    [tasks]
  );

  /** Задачи с заполненными difficulty и domain — один источник правды для списка и счётчика */
  const displayedTasks = useMemo(
    () => filteredTasks.filter((t) => t.difficulty && t.domain),
    [filteredTasks]
  );

  if (loading) return <div className="p-6 text-center">Загрузка задач...</div>;

  const filtersContent = (
    <>
      <div className="flex items-center justify-between">
        <div className="text-xs text-neutral-text/70">
          {activeFiltersCount > 0 ? `Активных фильтров: ${activeFiltersCount}` : "Выберите критерии отбора задач"}
        </div>
        {activeFiltersCount > 0 && (
          <button
            type="button"
            onClick={() => {
              setSelectedDomains([]);
              setSelectedStatuses([]);
              setSelectedDifficulties([]);
              setSelectedTags([]);
              setTagsExpanded(false);
            }}
            className="text-xs px-2 py-1 rounded-md border border-neutral-border text-neutral-text hover:bg-neutral-light transition-colors"
          >
            Сбросить
          </button>
        )}
      </div>

      {/* Фильтр по доменам: только кнопка «Документация» ведёт в /docs, клик по пилюлям — фильтр */}
      <div
        data-onboarding-id="domain-filter"
        className="relative group block p-4 border border-neutral-200 rounded-lg bg-white/70 hover:bg-neutral-50/60 transition-colors"
      >
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-neutral-text">Домен</h3>
          <button
            type="button"
            onClick={() => navigate("/docs")}
            className="flex items-center gap-1.5 px-2 py-1 text-xs text-primary hover:bg-primary/10 rounded transition-colors"
            title="Открыть документацию"
          >
            <BookOpen className="w-3.5 h-3.5" />
            <span>Документация</span>
          </button>
        </div>
        <p className="text-xs text-neutral-text/70 mb-2">Тематика (логистика, магазины, HR).</p>
        <div className="flex flex-wrap gap-2">
          {allDomains.map(domain => {
            const domainColor = domainColorMap[domain];
            return (
              <button
                key={domain}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedDomains(toggleArrayValue(selectedDomains, domain));
                }}
                className={`px-2 py-1 rounded-full text-sm border transition-colors cursor-pointer ${
                  selectedDomains.includes(domain)
                    ? `${domainColor}`
                    : `bg-transparent text-neutral-text border-neutral-border ${getDomainHoverClasses(domainColor)} active:opacity-80`
                }`}
              >
                {domain}
              </button>
            );
          })}
        </div>
      </div>

      {/* Кнопка для открытия дополнительных фильтров */}
      <button
        data-onboarding-id="additional-filters"
        onClick={() => setFiltersExpanded(!filtersExpanded)}
        className={`w-full flex items-center justify-between px-4 py-2 border border-neutral-200 bg-transparent hover:bg-neutral-50/50 transition-colors ${filtersExpanded ? "rounded-t-lg border-b-0" : "rounded-lg"}`}
      >
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-neutral-text" />
          <span className="text-sm font-semibold text-neutral-text">Дополнительные фильтры</span>
          {activeFiltersCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-primary/15 text-primary text-[11px] font-semibold">
              {activeFiltersCount}
            </span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-neutral-text transition-transform ${filtersExpanded ? "rotate-180" : ""}`} />
      </button>

      {/* Выпадающее меню с фильтрами */}
      {filtersExpanded && (
        <div className="p-4 border border-neutral-200 rounded-b-lg bg-white space-y-4">
          <p className="text-xs text-neutral-text/70 mb-1">По статусу, сложности и тегам.</p>
          {/* Фильтр по статусу */}
          <StatusFilter
            selectedStatuses={selectedStatuses}
            toggleStatus={status => setSelectedStatuses(toggleArrayValue(selectedStatuses, status))}
            statusCounts={statusCounts}
          />

          {/* Фильтр по сложности */}
          <DifficultyFilter
            allDifficulties={allDifficulties}
            selectedDifficulties={selectedDifficulties}
            toggleDifficulty={level => setSelectedDifficulties(toggleArrayValue(selectedDifficulties, level))}
            difficultyCounts={filteredDifficultyCounts}
          />

          {/* Облако тегов с раскрытием */}
          <div className="p-3 border border-neutral-border rounded-lg bg-neutral-light/40">
            <h3 className="text-sm font-semibold mb-2 text-neutral-text">Теги</h3>
            <div className="flex flex-wrap gap-2">
              {visibleTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => setSelectedTags(toggleArrayValue(selectedTags, tag))}
                  className={`px-2 py-1 rounded-full text-sm border transition-colors cursor-pointer ${
                    selectedTags.includes(tag)
                      ? "bg-neutral-600 text-white border-neutral-600"
                      : "bg-transparent text-neutral-600 border-neutral-200 hover:bg-neutral-50 hover:border-neutral-300"
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
            {hasMoreTags && (
              <button
                onClick={() => setTagsExpanded(!tagsExpanded)}
                className="mt-2 text-sm text-neutral-600 hover:text-neutral-800 hover:underline"
              >
                {tagsExpanded ? "Скрыть" : `Показать все (${allTags.length})`}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="w-full flex-1 flex flex-col min-h-0 overflow-hidden bg-neutral-light">
      <SEOHead
        title="SQL Тренажёр ClickHouse | 34+ практических заданий"
        description="Практикуйтесь в SQL на реальных задачах из e-commerce, логистики и HR. Мгновенная проверка решений, AI-подсказки. Работайте с настоящим ClickHouse в браузере."
        keywords="SQL тренажёр, ClickHouse задания, практика SQL онлайн, SQL задачи для начинающих, ClickHouse практика, интерактивный SQL, SQL упражнения, ClickHouse запросы, практика баз данных, SQL обучение через практику, ClickHouse задачи, SQL тесты, ClickHouse симулятор, онлайн SQL редактор, SQL практикум, ClickHouse примеры, SQL кейсы, реальные SQL задачи"
        structuredData={[
          generateBreadcrumbSchema([
            { name: "Главная", url: "/" },
            { name: "Тренажёр", url: "/simulator" }
          ])
        ]}
      />
      <div
        className={`w-full flex-1 flex flex-col min-h-0 overflow-hidden ${isMobile ? "py-4" : ""}`}
        style={{
          paddingLeft: isMobile ? "calc(16px + env(safe-area-inset-left, 0px))" : undefined,
          paddingRight: isMobile ? "calc(16px + env(safe-area-inset-right, 0px))" : undefined,
        }}
      >
        <div className="w-full flex-1 flex flex-col min-h-0 overflow-hidden">
          {isMobile ? (
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
              <main className="w-full">
              <div className="flex items-start justify-between flex-wrap gap-2 mb-4">
                <h1 className="text-2xl font-semibold">Список задач</h1>
                <div className="flex items-center gap-2">
                  <button
                    data-onboarding-id="mobile-filters"
                    onClick={() => {
                      setShowAITutor(false);
                      setFiltersOpen(true);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm bg-white border-2 border-neutral-border rounded-lg shadow-sm text-neutral-text hover:bg-primary/10 hover:border-primary/30 transition-colors"
                  >
                    <Filter className="w-4 h-4" />
                    Фильтры
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setFiltersOpen(false);
                      setTutorAsideMode("chat");
                      setShowAITutor(true);
                    }}
                    className={`px-3 py-2.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                      showAITutor && tutorAsideMode === "chat"
                        ? "bg-primary/15 text-primary border-2 border-primary/40"
                        : "bg-white border-2 border-primary/30 shadow-sm text-primary hover:bg-primary/10 hover:border-primary/40"
                    }`}
                    title="Открыть чат с AI Clickhouse Expert"
                    aria-label="AI Tutor"
                  >
                    <MessageCircle className="w-5 h-5" />
                    <span>AI Tutor</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setFiltersOpen(false);
                      setTutorAsideMode("roadmap");
                      setShowAITutor(true);
                    }}
                    className={`px-3 py-2.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-2 ${
                      showAITutor && tutorAsideMode === "roadmap"
                        ? "bg-primary/15 text-primary border-2 border-primary/40"
                        : "bg-white border-2 border-neutral-border shadow-sm text-neutral-text hover:bg-primary/10 hover:border-primary/30"
                    }`}
                    title="Построение роадмапа"
                    aria-label="Roadmap"
                  >
                    <Map className="w-5 h-5" />
                    <span>Roadmap</span>
                  </button>
                </div>
              </div>

              {/* Поиск и сортировка */}
              <div className="mb-4 flex flex-col gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-text/60" />
                  <input
                    data-onboarding-id="search-input"
                    type="text"
                    placeholder="Поиск"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-neutral-border rounded-lg bg-white text-neutral-text focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                </div>
                <div data-onboarding-id="sort-button">
                  <SortButton sortBy={sortBy} onSortChange={setSortBy} />
                </div>
              </div>

              <ul data-onboarding-id="tasks-list" className="space-y-3">
                {displayedTasks.map(task => (
                  <TaskCard
                    key={task.id}
                    task={task as Task & { difficulty: "easy" | "medium" | "hard"; domain: string }}
                    progress={progress[task.id]}
                    domainColor={domainColorMap[task.domain!] || "bg-neutral-light text-neutral-text"}
                    onDomainClick={(domain) => setSelectedDomains(toggleArrayValue(selectedDomains, domain))}
                    isDomainSelected={task.domain ? selectedDomains.includes(task.domain) : false}
                    onTagClick={(tag) => setSelectedTags(toggleArrayValue(selectedTags, tag))}
                    selectedTags={selectedTags}
                  />
                ))}
                {displayedTasks.length === 0 && (
                  <li className="text-neutral-text/70 text-center py-4">Нет задач по выбранным фильтрам</li>
                )}
              </ul>
              </main>
            </div>
          ) : (
            <div
                className="w-full flex-1 flex min-h-0 overflow-hidden"
                style={{ paddingRight: !isMobile && showAITutor ? Math.max(aiTutorResize.size, chatPanelMinWidth) : 0 }}
              >
              {/* Слева: скроллируемый контент (фильтры + список) */}
              <div className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden flex gap-6 py-6 px-6">
                {/* Фильтры слева только на широком desktop; на узком desktop скрываем */}
                <aside data-onboarding-id="filters-panel" className="hidden xl:block w-80 flex-shrink-0">
                  <div className="rounded-xl border-2 border-neutral-200 bg-white p-4 shadow-md shadow-neutral-200/50 space-y-4">
                    <h2 className="text-base font-semibold text-neutral-800 pb-1 border-b border-neutral-100">
                      Фильтры
                    </h2>
                    {filtersContent}
                  </div>
                </aside>

                {/* Список задач — центрируется в оставшемся пространстве между фильтрами и чатом */}
                <main
                  className="flex-1 min-w-0 max-w-3xl mx-auto pb-6"
                >
                  <h1 className="text-2xl font-semibold mb-4">Список задач</h1>

                  <div className="mb-4 flex gap-3">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-text/60" />
                      <input
                        data-onboarding-id="search-input"
                        type="text"
                        placeholder="Поиск"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="w-full pl-10 pr-3 py-2 border border-neutral-border rounded-lg bg-white text-neutral-text focus:outline-none focus:ring-2 focus:ring-primary/50"
                      />
                    </div>
                    <div data-onboarding-id="sort-button">
                      <SortButton sortBy={sortBy} onSortChange={setSortBy} />
                    </div>
                  </div>

                  {searchParams.get("task_ids") && (
                    <div className="mb-4 flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-primary/10 border border-primary/20 text-sm text-neutral-text">
                      <span>
                        {displayedTasks.length === 0
                          ? "По этой подборке ничего не найдено. Сбросьте подборку или ослабьте фильтры."
                          : `Показаны задачи из подборки чата (${displayedTasks.length})`}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          const next = new URLSearchParams(searchParams);
                          next.delete("task_ids");
                          setSearchParams(next, { replace: true });
                        }}
                        className="shrink-0 px-2 py-1 rounded text-primary hover:bg-primary/20 transition-colors"
                      >
                        Сбросить
                      </button>
                    </div>
                  )}

                  <ul data-onboarding-id="tasks-list" className="space-y-3 w-full">
                    {displayedTasks.map(task => (
                        <TaskCard
                          key={task.id}
                          task={task as Task & { difficulty: "easy" | "medium" | "hard"; domain: string }}
                          progress={progress[task.id]}
                          domainColor={domainColorMap[task.domain!] || "bg-neutral-light text-neutral-text"}
                          onDomainClick={(domain) => setSelectedDomains(toggleArrayValue(selectedDomains, domain))}
                          isDomainSelected={task.domain ? selectedDomains.includes(task.domain) : false}
                          onTagClick={(tag) => setSelectedTags(toggleArrayValue(selectedTags, tag))}
                          selectedTags={selectedTags}
                        />
                      ))}
                    {displayedTasks.length === 0 && (
                      <li className="text-neutral-text/70 text-center py-4">Нет задач по выбранным фильтрам</li>
                    )}
                  </ul>
                </main>

              </div>

              {/* Ресайзер AI Tutor (fixed, у левого края панели) */}
              {showAITutor && (
                <div
                  onMouseDown={aiTutorResize.onMouseDown}
                  className="hidden lg:flex fixed z-[56] justify-center cursor-col-resize"
                  style={{
                    width: "8px",
                    top: AI_TUTOR_DESKTOP_TOP,
                    bottom: 0,
                    right: Math.max(aiTutorResize.size, chatPanelMinWidth) - 4,
                  }}
                  aria-hidden
                >
                  <div className="w-1 rounded bg-transparent hover:bg-primary/40 transition-colors h-full" />
                </div>
              )}

              {/* AI Tutor — fixed, full height, перекрывает хедер */}
              {showAITutor && (
                <aside
                  ref={aiTutorResize.elementRef}
                  className="hidden lg:flex flex-col border-l border-neutral-border bg-white overflow-hidden"
                  style={{
                    position: "fixed",
                    top: AI_TUTOR_DESKTOP_TOP,
                    right: 0,
                    bottom: 0,
                    width: Math.max(aiTutorResize.size, chatPanelMinWidth),
                    zIndex: LAYERS.aiTutorPanel,
                  }}
                >
                  <AITutorTaskListPanel
                    user={user}
                    onClose={() => setShowAITutor(false)}
                    embedded
                    onMinWidthChange={setChatPanelMinWidth}
                    tasks={tasksForChat}
                    onApplyTaskSelection={(taskIds) => {
                      const next = new URLSearchParams(searchParams);
                      next.set("task_ids", taskIds.join(","));
                      setSearchParams(next, { replace: true });
                    }}
                    asideMode={tutorAsideMode}
                    onAsideModeChange={setTutorAsideMode}
                  />
                </aside>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Кнопки развернуть AI Tutor / Roadmap (desktop, когда панель свёрнута) */}
      {!isMobile && !showAITutor && (
        <div className="hidden lg:flex fixed right-0 top-[calc(var(--header-height,4.5rem)+0.5rem)] z-30 flex-col gap-1">
          <button
            type="button"
            onClick={() => {
              setTutorAsideMode("chat");
              setShowAITutor(true);
            }}
            className="flex h-12 min-w-[4.5rem] px-3 items-center justify-center gap-2 bg-white border-2 border-neutral-border border-r-0 rounded-l-lg shadow-md hover:bg-primary/5 hover:border-primary/40 transition-colors font-medium text-neutral-text hover:text-primary"
            aria-label="Открыть AI Tutor"
            title="Открыть AI Tutor"
          >
            <MessageCircle className="w-5 h-5 text-primary flex-shrink-0" />
            <span className="text-sm whitespace-nowrap">Чат</span>
          </button>
          <button
            type="button"
            onClick={() => {
              setTutorAsideMode("roadmap");
              setShowAITutor(true);
            }}
            className="flex h-12 min-w-[4.5rem] px-3 items-center justify-center gap-2 bg-white border-2 border-neutral-border border-r-0 rounded-l-lg shadow-md hover:bg-primary/5 hover:border-primary/40 transition-colors font-medium text-neutral-text hover:text-primary"
            aria-label="Открыть Roadmap"
            title="Построение роадмапа"
          >
            <Map className="w-5 h-5 text-primary flex-shrink-0" />
            <span className="text-sm whitespace-nowrap">Roadmap</span>
          </button>
        </div>
      )}

      {/* Mobile: AI Tutor fullscreen overlay */}
      {isMobile && showAITutor && (
        <div
          className="lg:hidden fixed inset-x-0 top-[var(--header-height,4.5rem)] bottom-0 flex flex-col bg-white border-t border-neutral-border"
          style={{ zIndex: LAYERS.mobileOverlay }}
        >
          <AITutorTaskListPanel
            user={user}
            onClose={() => setShowAITutor(false)}
            embedded
            asideMode={tutorAsideMode}
            onAsideModeChange={setTutorAsideMode}
          />
        </div>
      )}

      {/* Мобильный drawer фильтров */}
      {isMobile && filtersOpen && (
        <div className="fixed inset-0" style={{ zIndex: LAYERS.mobileDrawer }}>
          <div className="absolute inset-0 bg-black/30" onClick={() => setFiltersOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-[85%] max-w-xs bg-white shadow-xl p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-neutral-text">Фильтры</h2>
              <button
                onClick={() => setFiltersOpen(false)}
                className="text-neutral-text hover:text-primary"
              >
                ✕
              </button>
            </div>
            {filtersContent}
          </div>
        </div>
      )}
    </div>
  );
};
